// P3 sheets (frozen mockups-csh-v1): SCR-CSH-02 contribution, -03 owner withdrawal, -04 bank movement,
// -05 opening balances. Owner only. The database decides (balances, dates, closing); these checks only give
// instant feedback.
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BottomSheet, Button, Field, FormGrid, Input, Notice, SheetActions, Tabs } from '../../design/kit';
import { Refusal } from '../../data/auth';
import {
  CASH,
  bankTransfer,
  capitalContribution,
  ownerWithdrawal,
  postOpeningCash,
  type BusinessCash,
  type Direction,
} from '../../data/cash';
import { loadSettings } from '../../data/settings';
import type { Language } from '../../i18n';
import { messageKey } from '../../lib/errors';
import { businessToday, formatDate, formatMoney } from '../../lib/format';
import { useSession } from '../../app/session';
import { parseAmount } from '../catalog/common';

type FieldKey = 'amount' | 'party' | 'reason' | 'date' | 'form';
type Problem = { field: FieldKey; text: string };

const FIELD_OF: Record<string, FieldKey> = {
  INVALID_AMOUNT: 'amount',
  INSUFFICIENT_CASH: 'amount',
  CONTRIBUTOR_REQUIRED: 'party',
  RECIPIENT_REQUIRED: 'party',
  REASON_REQUIRED: 'reason',
  DATE_IN_FUTURE: 'date',
  DATE_BEFORE_START: 'date',
  DATE_REQUIRED: 'date',
};

/** Amount above zero with at most two decimals, or null. */
function money(text: string): number | null {
  const v = parseAmount(text);
  return v !== null && v > 0 && Math.round(v * 100) === v * 100 ? v : null;
}

function useCashWords() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language as Language;
  const fmt = (v: number | string) => formatMoney(v, lang);
  const refusal = (err: unknown): Problem => {
    if (!(err instanceof Refusal)) return { field: 'form', text: t(messageKey('SERVER_ERROR')) };
    const d = err.detail;
    const text =
      err.code === 'INSUFFICIENT_CASH'
        ? t('err.cash', { w: t(`cash.${String(d.account ?? 'garage')}`), a: fmt(String(d.balance ?? 0)) })
        : err.code === 'OPENING_CLOSED'
          ? t('err.openingcash')
          : t(messageKey(err.code));
    return { field: FIELD_OF[err.code] ?? 'form', text };
  };
  return { fmt, refusal, lang };
}

function useDone(onClose: () => void) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: CASH });
    void qc.invalidateQueries({ queryKey: ['settings', 'v_audit'] });
    onClose();
  };
}

function useStartDate(): string | undefined {
  return useQuery({ queryKey: ['settings', 'v_settings'], queryFn: loadSettings }).data?.business_start_date;
}

/** Design system: an amount field with the currency as its unit (README, Voice and content). */
export function MoneyField({
  label,
  value,
  onChange,
  hint,
  error,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  hint?: string | undefined;
  error?: string | undefined;
}) {
  const { t } = useTranslation();
  return (
    <Field label={label} hint={hint} error={error}>
      {({ id, describedBy, invalid }) => (
        <label className="yv-unit-input">
          <Input
            id={id}
            numeric
            inputMode="decimal"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            aria-describedby={describedBy}
            aria-invalid={invalid || undefined}
          />
          <span className="yv-unit-input__unit" aria-hidden="true">
            {t('currency.symbol')}
          </span>
        </label>
      )}
    </Field>
  );
}

function DateField({
  value,
  onChange,
  error,
}: {
  value: string;
  onChange: (v: string) => void;
  error?: string | undefined;
}) {
  const { t } = useTranslation();
  const start = useStartDate();
  return (
    <Field label={t('date')} error={error}>
      {({ id, describedBy, invalid }) => (
        <Input
          id={id}
          type="date"
          dir="ltr"
          min={start}
          max={businessToday()}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          aria-describedby={describedBy}
          aria-invalid={invalid || undefined}
        />
      )}
    </Field>
  );
}

function TextField({
  label,
  value,
  onChange,
  error,
  hint,
  ltr,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  error?: string | undefined;
  hint?: string | undefined;
  ltr?: boolean;
}) {
  return (
    <Field label={label} error={error} hint={hint}>
      {({ id, describedBy, invalid }) => (
        <Input
          id={id}
          dir={ltr ? 'ltr' : 'auto'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          aria-describedby={describedBy}
          aria-invalid={invalid || undefined}
        />
      )}
    </Field>
  );
}

/** Instant date checks; the database repeats them. */
function dateProblem(t: (k: string) => string, date: string, start: string | undefined): Problem | null {
  if (!date) return { field: 'date', text: t('err.required') };
  if (date > businessToday()) return { field: 'date', text: t('err.datefuture') };
  if (start && date < start) return { field: 'date', text: t('err.beforestart') };
  return null;
}

/** SCR-CSH-02: money put into garage cash; neither sales nor profit (CAP-01). */
export function ContributionSheet({ cash, onClose }: { cash: BusinessCash; onClose: () => void }) {
  const { t } = useTranslation();
  const { name } = useSession();
  const { fmt, refusal } = useCashWords();
  const start = useStartDate();
  const done = useDone(onClose);
  const [amount, setAmount] = useState('');
  const [who, setWho] = useState(name);
  const [date, setDate] = useState(businessToday());
  const [note, setNote] = useState('');
  const [problem, setProblem] = useState<Problem>();
  const v = money(amount);
  const save = useMutation({
    mutationFn: () => capitalContribution(v ?? 0, who.trim(), date, note.trim()),
    onSuccess: done,
    onError: (e) => setProblem(refusal(e)),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (v === null) return setProblem({ field: 'amount', text: t('err.amount') });
    if (!who.trim()) return setProblem({ field: 'party', text: t('err.contributor') });
    const d = dateProblem(t, date, start);
    if (d) return setProblem(d);
    setProblem(undefined);
    save.mutate();
  };
  const err = (f: FieldKey) => (problem?.field === f ? problem.text : undefined);
  return (
    <BottomSheet title={t('s32.title')} open onClose={onClose} busy={save.isPending}>
      <form onSubmit={submit} noValidate className="yv-skeleton-stack">
        {problem?.field === 'form' && <Notice tone="danger">{problem.text}</Notice>}
        <MoneyField label={t('amount')} value={amount} onChange={setAmount} error={err('amount')} />
        <TextField label={t('contributor')} value={who} onChange={setWho} error={err('party')} />
        <DateField value={date} onChange={setDate} error={err('date')} />
        <TextField label={t('note')} value={note} onChange={setNote} />
        <p className="muted" data-testid="garage-after">
          {t('cash.garage')} {fmt(cash.garage)}
          {v !== null && ` → ${fmt(Number(cash.garage) + v)}`}
        </p>
        <SheetActions>
          <Button type="submit" variant="main" busy={save.isPending}>
            {t('contrib.save')}
          </Button>
          <Button onClick={onClose}>{t('cancel')}</Button>
        </SheetActions>
      </form>
    </BottomSheet>
  );
}

/** SCR-CSH-03: money the owner takes out of garage cash; not an expense (CAP-02). */
export function WithdrawalSheet({ cash, onClose }: { cash: BusinessCash; onClose: () => void }) {
  const { t } = useTranslation();
  const { name } = useSession();
  const { fmt, refusal } = useCashWords();
  const start = useStartDate();
  const done = useDone(onClose);
  const [amount, setAmount] = useState('');
  const [who, setWho] = useState(name);
  const [reason, setReason] = useState('');
  const [date, setDate] = useState(businessToday());
  const [problem, setProblem] = useState<Problem>();
  const v = money(amount);
  const save = useMutation({
    mutationFn: () => ownerWithdrawal(v ?? 0, who.trim(), reason.trim(), date),
    onSuccess: done,
    onError: (e) => setProblem(refusal(e)),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (v === null) return setProblem({ field: 'amount', text: t('err.amount') });
    if (v > Number(cash.garage)) {
      return setProblem({ field: 'amount', text: t('err.cash', { w: t('cash.garage'), a: fmt(cash.garage) }) });
    }
    if (!who.trim()) return setProblem({ field: 'party', text: t('err.recipient') });
    if (!reason.trim()) return setProblem({ field: 'reason', text: t('err.reason') });
    const d = dateProblem(t, date, start);
    if (d) return setProblem(d);
    setProblem(undefined);
    save.mutate();
  };
  const err = (f: FieldKey) => (problem?.field === f ? problem.text : undefined);
  return (
    <BottomSheet title={t('s33.title')} open onClose={onClose} busy={save.isPending}>
      <form onSubmit={submit} noValidate className="yv-skeleton-stack">
        {problem?.field === 'form' && <Notice tone="danger">{problem.text}</Notice>}
        <Notice tone="info">{t('withdraw.hint')}</Notice>
        <MoneyField
          label={t('amount')}
          value={amount}
          onChange={setAmount}
          hint={t('available', { a: fmt(cash.garage) })}
          error={err('amount')}
        />
        <TextField label={t('recipient')} value={who} onChange={setWho} error={err('party')} />
        <TextField label={t('reason')} value={reason} onChange={setReason} error={err('reason')} />
        <DateField value={date} onChange={setDate} error={err('date')} />
        <SheetActions>
          <Button type="submit" variant="main" busy={save.isPending}>
            {t('withdraw.save')}
          </Button>
          <Button onClick={onClose}>{t('cancel')}</Button>
        </SheetActions>
      </form>
    </BottomSheet>
  );
}

/** SCR-CSH-04: garage cash to the bank or back; business cash unchanged (CAP-03). */
export function BankSheet({ cash, onClose }: { cash: BusinessCash; onClose: () => void }) {
  const { t } = useTranslation();
  const { fmt, refusal } = useCashWords();
  const start = useStartDate();
  const done = useDone(onClose);
  const [direction, setDirection] = useState<Direction>('to_bank');
  const [amount, setAmount] = useState('');
  const [reference, setReference] = useState('');
  const [date, setDate] = useState(businessToday());
  const [problem, setProblem] = useState<Problem>();
  const v = money(amount);
  const source = direction === 'to_bank' ? 'garage' : 'bank';
  const available = cash[source];
  const save = useMutation({
    mutationFn: () => bankTransfer(direction, v ?? 0, reference.trim(), date),
    onSuccess: done,
    onError: (e) => setProblem(refusal(e)),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (v === null) return setProblem({ field: 'amount', text: t('err.amount') });
    if (v > Number(available)) {
      return setProblem({ field: 'amount', text: t('err.cash', { w: t(`cash.${source}`), a: fmt(available) }) });
    }
    const d = dateProblem(t, date, start);
    if (d) return setProblem(d);
    setProblem(undefined);
    save.mutate();
  };
  const err = (f: FieldKey) => (problem?.field === f ? problem.text : undefined);
  return (
    <BottomSheet title={t('s34.title')} open onClose={onClose} busy={save.isPending}>
      <form onSubmit={submit} noValidate className="yv-skeleton-stack">
        {problem?.field === 'form' && <Notice tone="danger">{problem.text}</Notice>}
        <Tabs
          label={t('direction')}
          value={direction}
          onChange={(d) => {
            setDirection(d as Direction);
            setProblem(undefined);
          }}
          items={[
            { id: 'to_bank', label: t('dir.tobank') },
            { id: 'to_garage', label: t('dir.togarage') },
          ]}
        />
        <MoneyField
          label={t('amount')}
          value={amount}
          onChange={setAmount}
          hint={t('available', { a: fmt(available) })}
          error={err('amount')}
        />
        <TextField label={t('reference')} value={reference} onChange={setReference} hint={t('reference.hint')} ltr />
        <DateField value={date} onChange={setDate} error={err('date')} />
        <Notice tone="info">{t('bank.hint')}</Notice>
        <SheetActions>
          <Button type="submit" variant="main" busy={save.isPending}>
            {t('bank.save')}
          </Button>
          <Button onClick={onClose}>{t('cancel')}</Button>
        </SheetActions>
      </form>
    </BottomSheet>
  );
}

/** SCR-CSH-05: garage cash and bank at go-live, once (ADR-093, ADR-096). */
export function OpeningCashSheet({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const { refusal, lang } = useCashWords();
  const start = useStartDate();
  const done = useDone(onClose);
  const [garage, setGarage] = useState('');
  const [bank, setBank] = useState('');
  const [problem, setProblem] = useState<Problem>();
  const value = (text: string) => {
    if (!text.trim()) return 0;
    const v = parseAmount(text);
    return v !== null && v >= 0 && Math.round(v * 100) === v * 100 ? v : null;
  };
  const g = value(garage);
  const b = value(bank);
  const save = useMutation({
    mutationFn: () => postOpeningCash(g ?? 0, b ?? 0),
    onSuccess: done,
    onError: (e) => setProblem(refusal(e)),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (g === null || b === null) return setProblem({ field: 'amount', text: t('err.amount') });
    if (g === 0 && b === 0) return setProblem({ field: 'form', text: t('err.bothzero') });
    setProblem(undefined);
    save.mutate();
  };
  return (
    <BottomSheet title={t('s35.title')} open onClose={onClose} busy={save.isPending}>
      <form onSubmit={submit} noValidate className="yv-skeleton-stack">
        <Notice tone="info">{t('openingcash.hint')}</Notice>
        <FormGrid>
          <MoneyField
            label={t('cash.garage')}
            value={garage}
            onChange={setGarage}
            error={problem?.field === 'amount' ? problem.text : undefined}
          />
          <MoneyField label={t('cash.bank')} value={bank} onChange={setBank} />
        </FormGrid>
        {problem?.field === 'form' && <Notice tone="danger">{problem.text}</Notice>}
        <Field label={t('date')} hint={t('startdate')}>
          {({ id, describedBy }) => (
            <Input
              id={id}
              dir="ltr"
              readOnly
              value={start ? formatDate(start, lang) : ''}
              aria-describedby={describedBy}
            />
          )}
        </Field>
        <SheetActions>
          <Button type="submit" variant="main" busy={save.isPending}>
            {t('openingcash.save')}
          </Button>
          <Button onClick={onClose}>{t('cancel')}</Button>
        </SheetActions>
      </form>
    </BottomSheet>
  );
}
